import _ from 'lodash';

import store from '../../state';
import { setDraggingWallResizer } from '../../state/scene/sceneActions.ts';
import constants from '../../settings/constants';
import { ceil } from '../../util/round';
import { actionSetDirtyConfiguration } from '../../state/vpc/vpcActions';
import { thunkSetWall } from '../../state/tac/tacThunks';

export default class DraggableWallResizer {
  static draggingWallResizer = false;

  constructor({
    moveableAxes,
    ratio,
    canvasContainer,
    canvasHeight,
    prevWallSize,
    itemContainerLimits,
    wallResizerOffset,
  }) {
    this.itemContainerLimits = itemContainerLimits;
    this.prevWallSize = prevWallSize;
    this.canvasHeight = canvasHeight;
    this.canvasContainer = canvasContainer;
    this.moveableAxes = moveableAxes;
    this.wallResizerOffset = wallResizerOffset;
    this.ratio = ratio;
    this.dragging = false;
    this.draggingPosition = null;
    this.prevEmittedWallSize = null;

    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
  }

  update({ prevWallSize }) {
    this.prevWallSize = prevWallSize;
    if (DraggableWallResizer.draggingWallResizer && !this.dragging) {
      this.getDisplayObject().cursor = null;
    } else if (this.dragging) {
      this.getDisplayObject().cursor = 'grabbing';
    } else {
      this.getDisplayObject().cursor = this.getHoverCursor();
    }
  }

  onPointerDown(e) {
    this.pointerOffset = {
      x: e.data.global.x - this.getPosition().x,
      y: e.data.global.y - this.getPosition().y,
    };
    DraggableWallResizer.draggingWallResizer = true;
    store.dispatch(
      setDraggingWallResizer(DraggableWallResizer.draggingWallResizer)
    );
    this.draggingPosition = e.data.global;
    this.dragging = true;
    document.body.style.cursor = 'grabbing';
    this.getDisplayObject().cursor = 'grabbing';
  }

  onPointerUp() {
    if (this.dragging) {
      DraggableWallResizer.draggingWallResizer = false;
      store.dispatch(
        setDraggingWallResizer(DraggableWallResizer.draggingWallResizer)
      );
      this.draggingPosition.x -= this.pointerOffset.x;
      this.draggingPosition.y -= this.pointerOffset.y;
      store.dispatch(
        thunkSetWall(this._getWallMeasurement(this.draggingPosition), {
          origin: 'drag',
          isPersistent: true,
        })
      );
      this.dragging = false;
      store.dispatch(actionSetDirtyConfiguration(true));
    }
    document.body.style.cursor = '';
    this.getDisplayObject().cursor = this.getHoverCursor();
  }

  onPointerMove(e) {
    if (e.data.originalEvent && e.data.originalEvent.cancelable) {
      e.data.originalEvent.preventDefault();
    }

    if (this.dragging) {
      this.draggingPosition = e.data.global;

      this.draggingPosition.x -= this.pointerOffset.x;
      this.draggingPosition.y -= this.pointerOffset.y;

      const wallSize = this._getWallMeasurement(this.draggingPosition);

      if (
        !this.prevEmittedWallSize ||
        Math.abs(wallSize.width - this.prevEmittedWallSize.width) >= 10 ||
        Math.abs(wallSize.height - this.prevEmittedWallSize.height) >= 10
      ) {
        store.dispatch(
          thunkSetWall(this._getWallMeasurement(this.draggingPosition), {
            shouldReport: false,
          })
        );
        this.prevEmittedWallSize = wallSize;
      }
    }
  }

  /**
   * @param {number} position.x Drag dot x coordinate in px.
   * @param {number} position.y Drag dot y coordinate in px.
   *
   * @returns {object} {width, height} in mm.
   */
  _getWallMeasurement(position) {
    const width = ceil(
      this.moveableAxes.x
        ? parseInt((position.x - this.wallResizerOffset.x) / this.ratio)
        : this.prevWallSize.width / this.ratio,
      10
    );

    const height = ceil(
      this.moveableAxes.y
        ? parseInt(
            (this.canvasHeight -
              this.wallResizerOffset.y -
              (position.y - this.canvasContainer.top)) /
              this.ratio
          )
        : this.prevWallSize.height / this.ratio,
      10
    );

    return {
      width: _.clamp(
        width,
        Math.max(
          constants.WALL.width.min,
          this.itemContainerLimits ? this.itemContainerLimits.max.x : 0
        ),
        this.itemContainerLimits?.blockedX
          ? this.itemContainerLimits.max.x
          : constants.WALL.width.max
      ),
      height: _.clamp(
        height,
        Math.max(
          constants.WALL.height.min,
          this.itemContainerLimits ? this.itemContainerLimits.max.y : 0
        ),
        constants.WALL.height.max
      ),
    };
  }
}
